import { FC, useMemo, useState, useEffect } from 'react';
import FiltersSidebarContainer from 'shared/components/FilterSidebar/FiltersSidebarContainer';
import { useGovernanceTags } from '../../hooks/useGovernanceTags';
import { useRootStore } from 'app/contexts/RootStoreContext';
import { ALL_FILTERS_MAP } from 'shared/components/FilterSidebar/constants';
import { COLUMN_NAMES } from '../../constants/columnNames';
import { FilterSidebarProps, FilterOption } from '../../types';
import styles from './FilterSidebar.module.scss';
import { SearchFilters } from 'recommendationsNew/components/heatMap/heatMapFilters/additionalFilters/searchFilters';
import Button from 'shared/components/andtComponents/Button';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { useInvoiceFilters } from 'invoices/contexts/InvoiceFiltersContext';

const FILTER_PROPERTY_MAP = {
  [ALL_FILTERS_MAP.RESOURCEID.id]: COLUMN_NAMES.RESOURCE_ID,
  [ALL_FILTERS_MAP.SERVICES.id]: COLUMN_NAMES.SERVICE,
  [ALL_FILTERS_MAP.REGION.id]: COLUMN_NAMES.REGION,
  [ALL_FILTERS_MAP.LINKEDACCID.id]: COLUMN_NAMES.LINKED_ACCOUNT,
  [ALL_FILTERS_MAP.CUSTOM_TAGS.id]: COLUMN_NAMES.TAGS,
  [ALL_FILTERS_MAP.GOVERNANCE_TAGS.id]: COLUMN_NAMES.GOVERNANCE_TAGS,
} as const;

const createGovernanceTagOptions = (distinctTagValues: Record<string, string[]> | null): FilterOption[] => {
  if (!distinctTagValues) {
    return [];
  }

  const options: FilterOption[] = [];
  for (const [tagKey, tagValues] of Object.entries(distinctTagValues)) {
    options.push({ value: tagKey, label: tagKey });
    for (const tagValue of tagValues) {
      options.push({
        value: `${tagKey}: ${tagValue}`,
        label: `${tagKey}: ${tagValue}`,
      });
    }
  }
  return options;
};

const createDistinctValuesMap = (
  governanceTagOptions: FilterOption[],
  filtersMap: Map<string, FilterOption[] | string[]>,
  getCustomTagsKeys: () => string[],
): Map<string, FilterOption[] | string[]> => {
  const valuesMap = new Map<string, FilterOption[] | string[]>();

  Object.entries(FILTER_PROPERTY_MAP).forEach(([filterId]) => {
    if (filterId === ALL_FILTERS_MAP.GOVERNANCE_TAGS.id) {
      valuesMap.set(filterId, governanceTagOptions);
    } else if (filterId === ALL_FILTERS_MAP.CUSTOM_TAGS.id) {
      valuesMap.set(filterId, getCustomTagsKeys());
    } else {
      valuesMap.set(filterId, filtersMap.get(filterId) || []);
    }
  });

  return valuesMap;
};

const createSelectedOptionsMap = (
  governanceTagsFilters: Record<string, FilterOption[]>,
): Map<string, FilterOption[]> => {
  const optionsMap = new Map<string, FilterOption[]>();
  if (governanceTagsFilters) {
    Object.keys(FILTER_PROPERTY_MAP).forEach((filterId) => {
      const filterValue = governanceTagsFilters[filterId];
      if (filterValue) {
        optionsMap.set(filterId, filterValue);
      }
    });
  }
  return optionsMap;
};

const GovernanceTagsFilterSidebar: FC<FilterSidebarProps> = ({ isOpen, onClose }) => {
  const [filtersSearchString, setFiltersSearchString] = useState('');
  const [tempSelectedOptionsMap, setTempSelectedOptionsMap] = useState<Map<string, FilterOption[]>>(new Map());
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(true);

  const { filtersValuesMap, getCustomTagsKeys } = useInvoiceFilters();

  const { usersStore } = useRootStore();
  const { governanceTagsFilters, setGovernanceTagsFilters, distinctTagValues } = useGovernanceTags();

  const governanceTagOptions = useMemo(() => createGovernanceTagOptions(distinctTagValues), [distinctTagValues]);
  const selectedOptionsMap = useMemo(() => createSelectedOptionsMap(governanceTagsFilters), [governanceTagsFilters]);
  const distinctValuesMap = useMemo(
    () => createDistinctValuesMap(governanceTagOptions, filtersValuesMap, getCustomTagsKeys),
    [filtersValuesMap, getCustomTagsKeys, governanceTagOptions],
  );

  useEffect(() => {
    if (isOpen) {
      const tempMap = new Map<string, FilterOption[]>();
      for (const [key, value] of selectedOptionsMap) {
        tempMap.set(key, [...value]);
      }
      setTempSelectedOptionsMap(tempMap);
      setIsApplyButtonDisabled(true);
    }
  }, [isOpen, selectedOptionsMap]);

  const distinctValuesProps = useMemo(() => {
    return {
      ...Array.from(distinctValuesMap.keys()).reduce((acc, key) => {
        acc[key] = {
          disableExclude: true,
          hideSelectAll: true,
        };
        return acc;
      }, {}),
      [ALL_FILTERS_MAP.GOVERNANCE_TAGS.id]: {
        showNotAllocated: false,
        disableExclude: true,
      },
      [ALL_FILTERS_MAP.RESOURCEID.id]: {
        forceHideSelectAll: true,
        disableExclude: true,
      },
      [ALL_FILTERS_MAP.CUSTOM_TAGS.id]: {
        showNotAllocated: false,
        disableExclude: true,
      },
    };
  }, [distinctValuesMap]);

  const handleFilterChange = (field: string, selectedOptions: FilterOption[]) => {
    const newTempSelectedOptionsMap = new Map(tempSelectedOptionsMap);
    newTempSelectedOptionsMap.set(field, selectedOptions);
    setTempSelectedOptionsMap(newTempSelectedOptionsMap);
    setIsApplyButtonDisabled(false);
  };

  const handleRemoveField = (field: string) => {
    const newTempSelectedOptionsMap = new Map(tempSelectedOptionsMap);
    newTempSelectedOptionsMap.delete(field);
    setTempSelectedOptionsMap(newTempSelectedOptionsMap);
    setIsApplyButtonDisabled(false);
  };

  const handleChangeFilterType = () => {
    // No-op for now since we don't support exclude functionality
  };

  const handleApplyFilters = () => {
    const newFilters = Object.fromEntries(tempSelectedOptionsMap);
    setGovernanceTagsFilters({
      ...governanceTagsFilters,
      ...newFilters,
    });
    setIsApplyButtonDisabled(true);
    onClose?.();
  };

  const handleClearFilters = () => {
    setTempSelectedOptionsMap(new Map());
    setGovernanceTagsFilters({});
    setIsApplyButtonDisabled(true);
    onClose?.();
  };

  return (
    <div className={`${styles.filterSidebarContainer} ${!isOpen ? styles.closed : ''}`}>
      <div className={styles.filterSidebarHeader}>
        <div className={styles.headerLeft}>
          <GenerateIcon iconName={ICONS.barsFilter.name} className={styles.filterIcon} />
          <h3>Filters</h3>
        </div>
        <div className={styles.headerRight}>
          <Button
            text="Reset"
            isTextButton
            automationid="reset-filters"
            onClick={handleClearFilters}
            className={styles.resetButton}
          />
          <Button
            text="Apply"
            isSecondary
            automationid="apply-filters"
            onClick={handleApplyFilters}
            disabled={isApplyButtonDisabled}
            className={styles.applyButton}
          />
          <Button
            text=""
            isTextButton
            icon={() => <GenerateIcon iconName={ICONS.xMark.name} />}
            onClick={onClose}
            className={styles.closeButton}
          />
        </div>
      </div>
      <div className={styles.filterSidebarContent}>
        <div className={styles.filtersSearch}>
          <SearchFilters
            placeholder="Search Filter"
            searchValue={filtersSearchString}
            onSearchChange={setFiltersSearchString}
          />
        </div>
        <div className={styles.filtersList}>
          <FiltersSidebarContainer
            fieldToFieldDistinctValuesMap={distinctValuesMap}
            selectedOptionsMap={tempSelectedOptionsMap}
            handleFilterChange={handleFilterChange}
            handleRemoveFieldFromFiltersValuesMap={handleRemoveField}
            excludedFiltersStatusMap={new Map()}
            handleChangeFilterType={handleChangeFilterType}
            currDispUserCloudAccountType={usersStore?.currDispUserCloudAccountType}
            fieldToFieldDistinctValuesProps={distinctValuesProps}
            showTitle={false}
            fieldsWidth={250}
            hideSearch={true}
          />
        </div>
      </div>
    </div>
  );
};

export default GovernanceTagsFilterSidebar;
